import type { ChangeEvent, FormEvent, KeyboardEvent, MouseEvent, RefObject } from 'react';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@c2fo/liquidity';
import { ArrowRightIcon, BanIcon, EarthEuropeIcon, QuestionCircleIcon } from '@c2fo/liquidity/icons';
import type { MarketSearchResult } from '../../../services/marketSearchService';
import type { DivisionSearchResult, UserSearchResult } from '../InsightsPage.types';
import { getDivisionPrimaryLabel, getDivisionSecondaryLabel, getDivisionTertiaryLabel } from '../mentionUtils';

interface InsightsInputComposerProps {
  inputValue: string;
  inputRef: RefObject<HTMLTextAreaElement>;
  inputContainerRef: RefObject<HTMLDivElement>;
  isThreadLoading: boolean;
  webSearchEnabled: boolean;
  userSearchResults: UserSearchResult[];
  isUserSearchLoading: boolean;
  isUserSearchOpen: boolean;
  userSearchActiveIndex: number;
  divisionSearchResults: DivisionSearchResult[];
  isDivisionSearchLoading: boolean;
  isDivisionSearchOpen: boolean;
  divisionSearchActiveIndex: number;
  marketSearchResults: MarketSearchResult[];
  isMarketSearchLoading: boolean;
  isMarketSearchOpen: boolean;
  marketSearchActiveIndex: number;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onToggleWebSearch: () => void;
  onInputChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onInputClick: (event: MouseEvent<HTMLTextAreaElement>) => void;
  onInputKeyUp: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onInputKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onOpenDataDictionary: () => void;
  onSelectUserResult: (selected: UserSearchResult) => void;
  onSelectDivisionResult: (selected: DivisionSearchResult) => void;
  onSelectMarketResult: (selected: MarketSearchResult) => void;
  onStop: () => void;
}

export const InsightsInputComposer = ({
  inputValue,
  inputRef,
  inputContainerRef,
  isThreadLoading,
  webSearchEnabled,
  userSearchResults,
  isUserSearchLoading,
  isUserSearchOpen,
  userSearchActiveIndex,
  divisionSearchResults,
  isDivisionSearchLoading,
  isDivisionSearchOpen,
  divisionSearchActiveIndex,
  marketSearchResults,
  isMarketSearchLoading,
  isMarketSearchOpen,
  marketSearchActiveIndex,
  onSubmit,
  onToggleWebSearch,
  onInputChange,
  onInputClick,
  onInputKeyUp,
  onInputKeyDown,
  onOpenDataDictionary,
  onSelectUserResult,
  onSelectDivisionResult,
  onSelectMarketResult,
  onStop,
}: InsightsInputComposerProps) => {
  return (
    <form onSubmit={onSubmit} className="input-form">
      <div className="input-container input-container-inline" ref={inputContainerRef}>
        <div className="textarea-wrapper">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`insights-tools-trigger${webSearchEnabled ? ' is-active' : ''}`}
                title="Tools"
                aria-label="Open tools menu"
                disabled={isThreadLoading}
              >
                +
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="insights-tools-menu">
              <DropdownMenuItem
                className={`insights-tools-menu-item${webSearchEnabled ? ' is-active' : ''}`}
                onSelect={onToggleWebSearch}
              >
                <span className="insights-tools-menu-item-icon" aria-hidden="true">
                  <EarthEuropeIcon className="h-4 w-4" />
                </span>
                <span>Web search</span>
                <span className="insights-tools-menu-item-state">{webSearchEnabled ? 'On' : 'Off'}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={onInputChange}
            onClick={onInputClick}
            onKeyUp={onInputKeyUp}
            onKeyDown={onInputKeyDown}
            placeholder="Ask a question..."
            aria-label="Insights prompt input"
            disabled={isThreadLoading}
            className="insights-textarea"
            rows={1}
          />

          <button
            type="button"
            className="textarea-help-button"
            onClick={onOpenDataDictionary}
            title="View available tables"
            aria-label="View available tables"
          >
            <QuestionCircleIcon className="h-5 w-5" />
          </button>
        </div>

        {isUserSearchOpen && (
          <div className="insights-mention-dropdown" role="listbox" aria-label="User search suggestions">
            {isUserSearchLoading ? (
              <div className="insights-mention-item insights-mention-item-muted">Searching…</div>
            ) : userSearchResults.length === 0 ? (
              <div className="insights-mention-item insights-mention-item-muted">No matches</div>
            ) : (
              userSearchResults.map((user, index) => {
                const isActive = index === userSearchActiveIndex;
                const label = `${user.firstName} ${user.lastName}`.trim();

                return (
                  <button
                    key={user.authServiceUuid}
                    type="button"
                    className={`insights-mention-item${isActive ? ' is-active' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onSelectUserResult(user);
                    }}
                  >
                    <div className="insights-mention-primary">{label || user.emailAddress}</div>
                    <div className="insights-mention-secondary">{user.emailAddress}</div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {isDivisionSearchOpen && (
          <div className="insights-mention-dropdown" role="listbox" aria-label="Division search suggestions">
            {isDivisionSearchLoading ? (
              <div className="insights-mention-item insights-mention-item-muted">Searching…</div>
            ) : divisionSearchResults.length === 0 ? (
              <div className="insights-mention-item insights-mention-item-muted">No matches</div>
            ) : (
              divisionSearchResults.map((division, index) => {
                const isActive = index === divisionSearchActiveIndex;
                const primary = getDivisionPrimaryLabel(division);
                const secondary = getDivisionSecondaryLabel(division);
                const tertiary = getDivisionTertiaryLabel(division);

                return (
                  <button
                    key={`${division.uuid}-${division.id}-${index}`}
                    type="button"
                    className={`insights-mention-item${isActive ? ' is-active' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onSelectDivisionResult(division);
                    }}
                  >
                    <div className="insights-mention-primary">{primary}</div>
                    {secondary && <div className="insights-mention-secondary">{secondary}</div>}
                    {tertiary && <div className="insights-mention-secondary">{tertiary}</div>}
                  </button>
                );
              })
            )}
          </div>
        )}

        {isMarketSearchOpen && (
          <div className="insights-mention-dropdown" role="listbox" aria-label="Market search suggestions">
            {isMarketSearchLoading ? (
              <div className="insights-mention-item insights-mention-item-muted">Searching…</div>
            ) : marketSearchResults.length === 0 ? (
              <div className="insights-mention-item insights-mention-item-muted">No matches</div>
            ) : (
              marketSearchResults.map((market, index) => {
                const isActive = index === marketSearchActiveIndex;

                return (
                  <button
                    key={`${market.marketUuid}-${market.marketId}-${index}`}
                    type="button"
                    className={`insights-mention-item${isActive ? ' is-active' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onSelectMarketResult(market);
                    }}
                  >
                    <div className="insights-mention-primary">{market.name}</div>
                    <div className="insights-mention-secondary">
                      UUID: {market.marketUuid} ID: {market.marketId}
                    </div>
                    <div className="insights-mention-secondary">
                      {market.currency} • {market.isEnabled ? 'Enabled' : 'Disabled'}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {isThreadLoading ? (
          <Button
            key="stop-inline"
            type="button"
            onClick={onStop}
            variant="destructive"
            size="md"
            icon={<BanIcon />}
            title="Stop generation"
          >
            Stop
          </Button>
        ) : (
          <Button
            key="send-inline"
            type="submit"
            variant="cta"
            size="md"
            icon={<ArrowRightIcon />}
            disabled={!inputValue.trim()}
            title="Send message"
          >
            Send
          </Button>
        )}
      </div>
    </form>
  );
};
